import { Entity, UniqueEntityID } from '@/domain/core'
import { FuelType } from '@/domain/protocols/AircraftType'
import { FuelQuantityUnit, LinearMeasureUnit, WeightUnit } from '@/domain/protocols/Unit'

import { getMaximumWeightRuleViolations, isCrossMaximumWeightRule } from './maximumWeightsRules'

import { isSet } from '@/utils/validation'

export type MomentIndex = 1 | 10 | 100 | 1000 | 10000
export type StationType = 'seat' | 'cargo' | 'fuel'
/**
 * Linha da tabela de braço variável de um tanque, como o manual da aeronave a publica.
 *
 * `weight` é o **peso do combustível** na `weightUnit` do perfil, e não a quantidade. Repare na
 * assimetria com a `maxCapacity` do tanque, que é declarada na unidade de **combustível**: a coluna
 * não tem unidade própria e nunca atravessa densidade nenhuma. É o que a aritmética da linha pede
 * (ver `moment` abaixo) e é a leitura que o cliente móvel já usa.
 *
 * `moment` é o momento **cru**, em peso × braço das unidades do perfil — sem o índice de momento, que
 * é escala de exibição. Daí o braço da linha ser exatamente `moment / weight`: converter só um dos
 * dois escala o braço do tanque inteiro (PLA-338).
 */
export interface VariableArm {
  id: UniqueEntityID // UUID
  weight: number
  moment: number
}

export interface WBStations {
  id: UniqueEntityID // UUID
  name: string
  type: StationType
  fixedArm?: number
  maxCapacity?: number
  capacityUnit?: FuelQuantityUnit
  includeInBOW?: boolean
  predefinedWeight?: number
  armType?: 'fixed' | 'variable'
  variableArms?: VariableArm[]
  priority?: number
}

export interface WBEnvelope {
  id: UniqueEntityID // UUID
  weight: number
  arm?: number
  macPercent?: number
  /**
   * Posição do ponto no contorno do envelope, de 1 a N.
   *
   * Polígono é sequência, e o polígono do envelope é o veredito de CG — não só o desenho. O campo é
   * opcional porque a linha antiga (gravada antes da coluna existir) volta do servidor com ele
   * nulo: nesse caso o contorno é reconstruído pela varredura angular (`utils/wbEnvelopeOrder.ts`)
   * e persistido na gravação seguinte.
   */
  pointSequence?: number
}

export interface WBProfileAirplaneProps {
  aircraftId: UniqueEntityID
  wbProfileAirplaneModelId?: UniqueEntityID
  name: string
  reference: string
  weightUnit: WeightUnit
  armUnit: LinearMeasureUnit
  fuelQuantityUnit: FuelQuantityUnit
  momentIndex: MomentIndex
  lemac?: number
  macLength?: number
  basicEmptyWeight: number
  basicEmptyWeightArm?: number
  maxTakeoffWeight: number
  maxZeroFuelWeight?: number
  maxRampWeight?: number
  maxLandingWeight?: number

  stations: WBStations[]
  envelope: WBEnvelope[]

  createdAt: Date
  updatedAt: Date
  deletedAt?: Date
}

export interface WBProfileAirplanePojo extends WBProfileAirplaneProps {
  id: string
}

export class WBProfileAirplane extends Entity<WBProfileAirplaneProps, WBProfileAirplanePojo> {
  private constructor(props: WBProfileAirplaneProps, id?: UniqueEntityID) {
    super(props, id)
  }

  public static create(props: WBProfileAirplaneProps, id?: UniqueEntityID): WBProfileAirplane {
    const profile = new WBProfileAirplane(props, id)
    return profile
  }

  public get completed(): boolean {
    const seatCondition = this.props.stations?.some((station) => station.type === 'seat') || false
    const tankCondition = this.props.stations?.some((station) => station.type === 'fuel') || false
    const envelopeCondition =
      this.props.envelope?.length >= 4 &&
      this.props.envelope?.every((limit) => isSet(limit.weight) && (isSet(limit.arm) || isSet(limit.macPercent)))
    const conditions = [
      !!this.props.basicEmptyWeight,
      isSet(this.props.basicEmptyWeightArm),
      !!this.props.maxTakeoffWeight,
      // Só as regras contra o peso básico vazio reprovam: um máximo abaixo dele não deixa nenhum
      // carregamento passar. As cruzadas entre máximos são avisadas no passo dos pesos máximos e
      // seguem — reprovar por elas tirava da seleção de um P&B novo os perfis já gravados assim.
      !getMaximumWeightRuleViolations(this.props).some((rule) => !isCrossMaximumWeightRule(rule)),
      seatCondition,
      tankCondition,
      envelopeCondition
    ]
    return conditions.every((condition) => condition)
  }

  // getters and setters for properties:

  public get aircraftId(): UniqueEntityID {
    return this.props.aircraftId
  }

  public set aircraftId(aircraftId: UniqueEntityID) {
    this.props.aircraftId = aircraftId
  }

  public get wbProfileAirplaneModelId(): UniqueEntityID {
    return this.props.wbProfileAirplaneModelId
  }

  public set wbProfileAirplaneModelId(wbProfileAirplaneModelId: UniqueEntityID) {
    this.props.wbProfileAirplaneModelId = wbProfileAirplaneModelId
  }

  public get name(): string {
    return this.props.name
  }

  public set name(name: string) {
    this.props.name = name
  }

  public get reference(): string {
    return this.props.reference
  }

  public set reference(reference: string) {
    this.props.reference = reference
  }

  public get weightUnit(): WeightUnit {
    return this.props.weightUnit
  }

  public set weightUnit(weightUnit: WeightUnit) {
    this.props.weightUnit = weightUnit
  }

  public get armUnit(): LinearMeasureUnit {
    return this.props.armUnit
  }

  public set armUnit(armUnit: LinearMeasureUnit) {
    this.props.armUnit = armUnit
  }

  public get fuelQuantityUnit(): FuelQuantityUnit {
    return this.props.fuelQuantityUnit
  }

  public set fuelQuantityUnit(fuelQuantityUnit: FuelQuantityUnit) {
    this.props.fuelQuantityUnit = fuelQuantityUnit
  }

  public get momentIndex(): MomentIndex {
    return this.props.momentIndex
  }

  public set momentIndex(momentIndex: MomentIndex) {
    this.props.momentIndex = momentIndex
  }

  public get lemac(): number {
    return this.props.lemac
  }

  public set lemac(lemac: number) {
    this.props.lemac = lemac
  }

  public get macLength(): number {
    return this.props.macLength
  }

  public set macLength(macLength: number) {
    this.props.macLength = macLength
  }

  public get basicEmptyWeight(): number {
    return this.props.basicEmptyWeight
  }

  public set basicEmptyWeight(basicEmptyWeight: number) {
    this.props.basicEmptyWeight = basicEmptyWeight
  }

  public get basicEmptyWeightArm(): number | undefined {
    return this.props.basicEmptyWeightArm
  }

  public set basicEmptyWeightArm(basicEmptyWeightArm: number | undefined) {
    this.props.basicEmptyWeightArm = basicEmptyWeightArm
  }

  public get maxTakeoffWeight(): number {
    return this.props.maxTakeoffWeight
  }

  public set maxTakeoffWeight(maxTakeoffWeight: number) {
    this.props.maxTakeoffWeight = maxTakeoffWeight
  }

  public get maxZeroFuelWeight(): number | undefined {
    return this.props.maxZeroFuelWeight
  }

  public set maxZeroFuelWeight(maxZeroFuelWeight: number | undefined) {
    this.props.maxZeroFuelWeight = maxZeroFuelWeight
  }

  public get maxRampWeight(): number | undefined {
    return this.props.maxRampWeight
  }

  public set maxRampWeight(maxRampWeight: number | undefined) {
    this.props.maxRampWeight = maxRampWeight
  }

  public get maxLandingWeight(): number | undefined {
    return this.props.maxLandingWeight
  }

  public set maxLandingWeight(maxLandingWeight: number | undefined) {
    this.props.maxLandingWeight = maxLandingWeight
  }

  public get stations(): WBStations[] {
    return this.props.stations
  }

  public set stations(stations: WBStations[]) {
    this.props.stations = stations
  }

  public get envelope(): WBEnvelope[] {
    return this.props.envelope
  }

  public set envelope(envelope: WBEnvelope[]) {
    this.props.envelope = envelope
  }

  public get createdAt(): Date {
    return this.props.createdAt
  }

  public get updatedAt(): Date {
    return this.props.updatedAt
  }

  public get deletedAt(): Date | undefined {
    return this.props.deletedAt
  }

  public set deletedAt(deletedAt: Date | undefined) {
    this.props.deletedAt = deletedAt
  }

  public toPojo(): WBProfileAirplanePojo {
    return {
      id: this.id.toString(),
      aircraftId: this.props.aircraftId,
      wbProfileAirplaneModelId: this.props.wbProfileAirplaneModelId,
      name: this.props.name,
      weightUnit: this.props.weightUnit,
      armUnit: this.props.armUnit,
      momentIndex: this.props.momentIndex,
      fuelQuantityUnit: this.props.fuelQuantityUnit,
      lemac: this.props.lemac,
      macLength: this.props.macLength,
      basicEmptyWeight: this.props.basicEmptyWeight,
      basicEmptyWeightArm: this.props.basicEmptyWeightArm,
      maxTakeoffWeight: this.props.maxTakeoffWeight,
      maxZeroFuelWeight: this.props.maxZeroFuelWeight,
      maxRampWeight: this.props.maxRampWeight,
      maxLandingWeight: this.props.maxLandingWeight,
      stations: this.props.stations,
      envelope: this.props.envelope,
      createdAt: this.props.createdAt,
      updatedAt: this.props.updatedAt,
      deletedAt: this.props.deletedAt,
      reference: this.props.reference
    }
  }

  public static fromPojo(_pojo: WBProfileAirplanePojo): WBProfileAirplane {
    const profile = WBProfileAirplane.create(
      {
        aircraftId: _pojo.aircraftId,
        wbProfileAirplaneModelId: _pojo.wbProfileAirplaneModelId,
        name: _pojo.name,
        weightUnit: _pojo.weightUnit,
        armUnit: _pojo.armUnit,
        momentIndex: _pojo.momentIndex,
        fuelQuantityUnit: _pojo.fuelQuantityUnit,
        basicEmptyWeight: _pojo.basicEmptyWeight,
        basicEmptyWeightArm: _pojo.basicEmptyWeightArm,
        maxTakeoffWeight: _pojo.maxTakeoffWeight,
        maxZeroFuelWeight: _pojo.maxZeroFuelWeight,
        maxRampWeight: _pojo.maxRampWeight,
        maxLandingWeight: _pojo.maxLandingWeight,
        stations: _pojo.stations,
        envelope: _pojo.envelope,
        reference: _pojo.reference,
        lemac: _pojo.lemac,
        macLength: _pojo.macLength,
        createdAt: new Date(_pojo.createdAt),
        updatedAt: new Date(_pojo.updatedAt),
        deletedAt: new Date(_pojo.deletedAt)
      },
      new UniqueEntityID(_pojo.id)
    )
    return profile
  }
}
